import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../utils/api';
import { Button } from './ui/button';
import { Eye, EyeOff, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useAuth } from '../context/AuthContext';

export const DemoModeToggle = ({ onToggle }) => {
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();

  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    settingsAPI.getDemoMode().then(res => setVisible(res.data.demo_mode_visible)).catch(() => {});
  }, []);

  const handleToggle = async () => {
    setLoading(true);
    try {
      const newVal = !visible;
      await settingsAPI.setDemoMode(newVal);
      setVisible(newVal);
      toast.success(newVal ? 'Demo data visible' : 'Demo data hidden');
      onToggle?.();
    } catch (e) {
      toast.error('Failed to toggle demo mode');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await settingsAPI.deleteDemoData();
      const d = res.data.deleted || {};
      const total = Object.values(d).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${total} demo records`);
      setShowDeleteConfirm(false);
      onToggle?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete demo data');
    } finally {
      setDeleting(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <>
      <div className="flex items-center gap-2" data-testid="demo-mode-toggle">
        <Button
          variant="outline"
          size="sm"
          onClick={handleToggle}
          disabled={loading}
          className={`text-xs ${visible ? 'border-amber-300 text-amber-700 bg-amber-50' : 'border-slate-300 text-slate-500'}`}
        >
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : visible ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
          {visible ? 'Demo Visible' : 'Demo Hidden'}
        </Button>
        {isSuperAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            className="text-xs border-red-200 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3 mr-1" /> Purge Demo
          </Button>
        )}
      </div>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete All Demo Data?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">This will permanently remove all records flagged as demo data (clients, sites, prospects). This cannot be undone.</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)} className="flex-1">Cancel</Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting} className="flex-1" data-testid="confirm-purge-demo">
              {deleting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
              Delete Forever
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
